import _extends from "@babel/runtime/helpers/extends";
import _fragment_AtlassianUiModificationsCore_RootFragment from "./__generated__/fragment_AtlassianUiModificationsCore_RootFragment.graphql";
// react-magnetic-di - temporary comment, for auto dependency injection in tests
import React, { useEffect, useMemo } from 'react';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { useAdjustmentsContextActions } from '../../../controllers/adjustments-context';
import { ExecutionContextProvider } from '../../../controllers/execution-context';
import { useViewType } from '../../../controllers/issue-adjustments/hooks/use-view-type';
import { TenantContextProvider } from '../../../controllers/tenant-context';
import { isExecutionContextSupported, isTriggerPointSupported } from '../../../controllers/view-configuration';
import { AppsRenderer } from './apps-renderer';
import { useAppsToRender } from './apps-renderer/use-apps-to-render';
import { useContextCleanup } from './use-contexts-cleanup';
import { useEnrichedExecutionContext } from './use-enriched-execution-context';

/**
 * UI modifications root component with Relay fragment.
 *
 * The purpose is to use it as a part of a parent query.
 */
export var UiModificationsRootFragment = function UiModificationsRootFragment(props) {
  var executionContext = useEnrichedExecutionContext({
    executionContext: props.executionContext
  });
  var executionContextKey = props.executionContextKey,
    triggerPointKey = props.triggerPointKey;
  var viewType = useViewType();
  var _useAdjustmentsContex = useAdjustmentsContextActions(),
    setShouldUiModificationsLoad = _useAdjustmentsContex.setShouldUiModificationsLoad;
  var shouldLoad = executionContext && executionContextKey && isTriggerPointSupported(viewType, triggerPointKey) && isExecutionContextSupported(executionContext);
  useEffect(function () {
    // `executionContextKey` has to be the dependency of the useEffect
    // to set the shouldUiModificationsLoad state according to context changes
    // that is represented by the `executionContextKey`
    if (shouldLoad) {
      setShouldUiModificationsLoad(shouldLoad ? 'yes' : 'no');
    }
  }, [setShouldUiModificationsLoad, shouldLoad, executionContextKey]);
  if (!shouldLoad) {
    return null;
  }
  return /*#__PURE__*/React.createElement(RootFragmentWithCleanup, _extends({}, props, {
    executionContext: executionContext,
    executionContextKey: executionContextKey
  }));
};

/**
 * It is separated from
 * - `UiModificationsRootFragment` to run cleanup only when we know that we want to render UIM
 * - `RootFragment` to run cleanup imidiately without awaiting for the new Relay data when context changed
 */
var RootFragmentWithCleanup = function RootFragmentWithCleanup(props) {
  var cloudId = props.cloudId,
    activationId = props.activationId,
    executionContextKey = props.executionContextKey,
    triggerPointKey = props.triggerPointKey,
    executionContext = props.executionContext,
    numberOfSupportedFields = props.numberOfSupportedFields,
    resetFormFieldsCounter = props.resetFormFieldsCounter;
  useContextCleanup({
    executionContextKey: executionContextKey,
    numberOfSupportedFields: numberOfSupportedFields,
    resetFormFieldsCounter: resetFormFieldsCounter
  });
  var tenantContext = useMemo(function () {
    return {
      cloudId: cloudId,
      activationId: activationId
    };
  }, [cloudId, activationId]);
  var analyticsContextData = useMemo(function () {
    return {
      attributes: {
        triggerPointKey: triggerPointKey
      }
    };
  }, [triggerPointKey]);
  return /*#__PURE__*/React.createElement(AnalyticsContext, {
    data: analyticsContextData
  }, /*#__PURE__*/React.createElement(ExecutionContextProvider, {
    value: executionContext
  }, /*#__PURE__*/React.createElement(TenantContextProvider, {
    value: tenantContext
  }, /*#__PURE__*/React.createElement(RootFragment, props))));
};
var RootFragment = function RootFragment(_ref) {
  var fragmentRef = _ref.fragmentRef,
    ForgeAppRenderer = _ref.ForgeAppRenderer,
    executionContextKey = _ref.executionContextKey;
  var response = useFragment( /* eslint-disable @atlassian/relay/unused-fields */_fragment_AtlassianUiModificationsCore_RootFragment, /* eslint-enable @atlassian/relay/unused-fields */
  fragmentRef);
  var appsToRender = useAppsToRender({
    data: response,
    executionContextKey: executionContextKey
  });
  return /*#__PURE__*/React.createElement(AppsRenderer, {
    apps: appsToRender,
    ForgeAppRenderer: ForgeAppRenderer
  });
};